import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';

const INFO_EMAIL = 'info@voxelary.com';

const inputBase =
  'w-full bg-transparent border-b border-white/30 py-2.5 px-0 text-sm text-white placeholder-white/40 focus:outline-none focus:border-voxelary-400 focus:placeholder-white/60 transition-colors duration-200';

/** Lightweight contact for the holding site. Old partnership page content is preserved in git history / archive files for later. */
const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    organization: '',
    message: '',
    consent: false,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!formData.fullName || !formData.email || !formData.message || !formData.consent) {
      alert('Please fill out all required fields and accept the terms.');
      return;
    }
    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.set('form-name', 'contact');
      Object.entries(formData).forEach(([key, value]) => {
        body.set(key, String(value));
      });
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setIsSubmitted(true);
    } catch {
      alert('Something went wrong. Please email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20 px-6 sm:px-8">
      <div className="max-w-xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-voxelary-300 text-xs font-medium tracking-[0.2em] uppercase mb-4">
            Contact
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
            Let's talk
          </h1>
          <p className="text-white/60 text-sm sm:text-base leading-relaxed mb-2">
            Voxelary is focused on the AI-driven future of medicine.
          </p>
          <p className="text-white/45 text-sm mb-10">
            Reach us at{' '}
            <a href={`mailto:${INFO_EMAIL}`} className="text-white/80 hover:text-white transition-colors">
              {INFO_EMAIL}
            </a>
          </p>

          {isSubmitted ? (
            <div className="py-12 text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-voxelary-500/20">
                <CheckCircle className="w-6 h-6 text-voxelary-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Message sent</h3>
              <p className="text-white/50 text-sm">We'll be in touch soon.</p>
            </div>
          ) : (
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <input type="hidden" name="form-name" value="contact" />
              <div style={{ display: 'none' }}>
                <label>
                  Don't fill this out: <input name="bot-field" />
                </label>
              </div>

              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className={inputBase}
                placeholder="Full name*"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={inputBase}
                placeholder="Email*"
              />
              <input
                type="text"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                className={inputBase}
                placeholder="Organization"
              />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={4}
                className={`${inputBase} resize-none min-h-[96px]`}
                placeholder="How can we help?*"
              />

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
                <span className="text-white/45 text-xs leading-relaxed">
                  I agree to be contacted about this inquiry.
                </span>
              </label>

              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center px-8 py-3.5 bg-white text-voxelary-900 font-semibold text-sm hover:bg-white/90 transition-colors disabled:opacity-60"
              >
                {isSubmitting ? 'Sending…' : 'Send message'}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
